import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  CheckCircle2,
  Sparkles,
  BookOpen,
  FileText,
  FileSignature,
  FileLock2,
  ClipboardList,
  Bell,
  ShieldCheck,
  ArrowRight,
  Lock,
  Clock,
  Mail,
  Building2,
  Info,
  Search,
  Cpu,
} from "lucide-react";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import TestimonialsSection from "@/components/TestimonialsSection";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUYE = [
  {
    icon: FileText,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-700",
    title: "Política de Privacidad (12 ítems Art. 14 ter)",
    desc: "Cubre los 12 requisitos exactos del Art. 14 ter de la Ley 21.719. Generada con tus datos, lista para publicar.",
    tooltip: "Cumple uno-a-uno los 12 ítems que la Ley 21.719 exige publicar en tu sitio: identificación del responsable, canal de notificaciones, categorías de datos, base de legitimidad, medidas de seguridad (Decreto 7/2023 Min. Segpres), derechos ARCO + recurrir a la APDP, transferencias internacionales con Cláusulas Contractuales Modelo (Min. Economía dic 2025), plazos de conservación, fuente de los datos, retiro del consentimiento y decisiones automatizadas. ~1.500 palabras, ~7 min de entrevista.",
    badge: "Más solicitado",
  },
  {
    icon: FileSignature,
    iconBg: "bg-violet-50",
    iconColor: "text-violet-700",
    title: "Cláusula de Consentimiento",
    desc: "Texto legal listo para tus formularios web (contacto, registro, newsletter, compra).",
    tooltip: "Texto corto que va junto al checkbox de tus formularios. Asegura que el consentimiento sea expreso, libre, informado y específico — los 4 requisitos que exige el art. 12 de la Ley 21.719. Incluye HTML sugerido para pegar directo en tu sitio.",
  },
  {
    icon: FileLock2,
    iconBg: "bg-amber-50",
    iconColor: "text-amber-700",
    title: "Contrato con Encargado (DPA)",
    desc: "Modelo para firmar con tus proveedores que tratan datos por ti (hosting, mailing, CRM).",
    tooltip: "Exigido por el art. 15 de la Ley 21.719. Sin este contrato, eres responsable por todo lo que haga tu proveedor con los datos. Cubre confidencialidad, sub-encargados, transferencias internacionales, notificación de brechas en 72h y devolución/destrucción al terminar.",
  },
  {
    icon: ClipboardList,
    iconBg: "bg-rose-50",
    iconColor: "text-rose-700",
    title: "Catálogo de Datos Personales (RAT)",
    desc: "Documento interno OBLIGATORIO bajo el art. 16 + Art. 14 ter d) i) j). Lo primero que la APDP puede pedir en una fiscalización.",
    tooltip: "La guía oficial del gobierno (Min. Segpres, dic 2025) lo llama 'Catálogo de Datos Personales' (también conocido como RAT). Es la 'declaración jurada' de cómo tratas datos. La APDP puede pedírtelo sin previo aviso. Casi ninguna PYME chilena lo tiene. Lo armamos con tablas por cada actividad detectada en tu onboarding.",
    badge: "Obligatorio art. 16",
  },
  {
    icon: Cpu,
    iconBg: "bg-fuchsia-50",
    iconColor: "text-fuchsia-700",
    title: "Cláusula de Decisiones Automatizadas",
    desc: "Si usas chatbot IA, A/B testing o recommendation engines, debes declarar la lógica y consecuencias al titular (Art. 14 ter literal 12).",
    tooltip: "El Art. 14 ter literal 12 te obliga a informar al titular si tu sitio usa decisiones automatizadas o elaboración de perfiles (chatbot IA, A/B testing, scoring crediticio, recommendation engines, etc.). Generamos la cláusula con la lógica aplicada y las consecuencias previstas para cada servicio que detectamos en tu sitio.",
    badge: "Nuevo · Art. 14 ter",
  },
  {
    icon: Search,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-700",
    title: "Deep Scan — análisis multi-página",
    desc: "Escanea hasta 5 páginas internas (home + privacidad + cookies + contacto + checkout) y consolida los trackers ocultos en formularios y checkout.",
    tooltip: "El scan gratuito solo cubre la URL que pegas. Deep Scan auto-descubre y analiza las 5 páginas más críticas de tu sitio para detectar pixels que solo cargan en checkout, formularios o páginas internas. Entrega recomendaciones específicas por cada URL con el artículo aplicable.",
    badge: "Pro exclusivo",
  },
  {
    icon: BookOpen,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-700",
    title: "Guías por plataforma",
    desc: "Paso a paso para WordPress, Shopify y más: banner de cookies, bloqueo de scripts, derechos ARCO.",
    tooltip: "Tutoriales con screenshots y código listo para copiar. Cubren cómo instalar un banner de consentimiento conforme a la Ley 21.719, bloquear scripts de terceros antes del consentimiento, habilitar un canal ARCO funcional y validar que tu sitio cumple.",
  },
  {
    icon: Bell,
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-700",
    title: "Alertas por email",
    desc: "Te avisamos cuando hay una novedad importante de la ley o un cambio regulatorio que afecta a tu PYME.",
    tooltip: "Monitoreamos los pronunciamientos de la APDP, reglamentos, reinterpretaciones y novedades de la Ley 21.719. Cuando algo te afecta, recibes un correo con qué cambió, qué debes hacer y un link para actualizar tus documentos en 2 clics.",
  },
  {
    icon: Building2,
    iconBg: "bg-slate-100",
    iconColor: "text-slate-700",
    title: "Panel privado",
    desc: "Tus documentos, escaneos y respuestas de onboarding en un solo lugar, siempre accesibles.",
    tooltip: "Tu cuenta guarda toda la información: documentos generados (descargables las veces que quieras), escaneos anteriores con seguimiento de mejoras, respuestas del onboarding (no las repites para cada documento) y novedades de la ley que aún no leíste.",
  },
];

export default function ModuloPro() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [proInfo, setProInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pro/info`).then(({ data }) => setProInfo(data)).catch(() => {});
  }, []);

  const precio = proInfo?.precio_clp || 14990;

  // CTA contextual
  let ctaText, ctaAction, ctaIcon = ArrowRight, ctaTestId;
  if (!user) {
    ctaText = "Crear cuenta y empezar";
    ctaAction = () => navigate("/registro");
    ctaTestId = "mp-cta-register";
  } else if (user.es_pro) {
    ctaText = "Generar mi documento ahora";
    ctaAction = () => navigate("/cuenta/generar");
    ctaTestId = "mp-cta-generate";
  } else {
    ctaText = `Pagar $${precio.toLocaleString("es-CL")} y desbloquear`;
    ctaAction = () => navigate("/cuenta");
    ctaTestId = "mp-cta-pay";
  }

  return (
    <>
      <SEO
        title={`Módulo Pro — Cumple la Ley 21.719 por $${precio.toLocaleString("es-CL")} · pago único`}
        description="Todo lo que tu PYME necesita para cumplir con la Ley N° 21.719: documentos legales generados con IA + guías por plataforma + alertas. Pago único $14.990 · acceso permanente."
        path="/modulo-pro"
        keywords="Módulo Pro Ley 21.719, política de privacidad Chile, cumplimiento PYME, $14.990"
        ogImage="https://leydedatoschile.com/og/og-modulo-pro.jpg"
        schemas={[
          orgSchema,
          breadcrumbSchema([{ name: "Inicio", path: "/" }, { name: "Módulo Pro", path: "/modulo-pro" }]),
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Módulo Pro — Ley de Datos Chile",
            description: "Acceso permanente a documentos legales + guías + alertas para la Ley 21.719 · pago único.",
            offers: { "@type": "Offer", priceCurrency: "CLP", price: String(precio), availability: "https://schema.org/InStock" },
          },
        ]}
      />

      {/* Hero — dark, mockup-driven, anchored price */}
      <section
        data-testid="mp-hero-section"
        className="relative bg-ink-950 text-white overflow-hidden border-b border-white/5"
      >
        <div className="absolute inset-0 bg-grid-pattern opacity-40 pointer-events-none" aria-hidden="true" />
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-20 lg:pt-24 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left copy + pricing card */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-7">
                <span className="inline-block w-6 h-px bg-blue-400/60" />
                Módulo Pro · pago único
              </div>
              <h1 data-testid="mp-hero-title" className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.02]">
                Cumple la Ley 21.719{' '}
                <em className="italic text-blue-300">sin abogados</em>, sin plantillas sueltas.
              </h1>
              <p className="mt-7 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
                Genera los 7 documentos que la APDP puede pedirte, audita 5 páginas internas con Deep Scan, recibe alertas cuando cambia la ley. Pago único de <strong className="text-white">${precio.toLocaleString("es-CL")} CLP</strong> · acceso permanente · sin cobros futuros.
              </p>

              {/* anchored price */}
              <div className="mt-10 grid grid-cols-3 gap-3 max-w-2xl">
                <PriceAnchor label="Abogado tradicional" amount="$250.000+" tone="muted" />
                <PriceAnchor label="Módulo Pro" amount={`$${precio.toLocaleString("es-CL")}`} tone="highlight" hint="pago único · permanente" />
                <PriceAnchor label="Multa máxima" amount="$1.300M" tone="warn" />
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <button
                  data-testid={ctaTestId}
                  onClick={ctaAction}
                  className="h-12 px-6 rounded-full bg-white text-slate-900 hover:bg-slate-100 active:scale-[0.98] font-semibold inline-flex items-center gap-2 transition-all"
                >
                  {ctaText}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/cumplimiento-art-14-ter"
                  className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5 transition-colors"
                >
                  Ver checklist Art. 14 ter →
                </Link>
              </div>
              <p className="mt-5 font-mono text-[11px] text-slate-500 uppercase tracking-widest">
                Pago único · Webpay · acceso permanente · alertas de la ley gratis de por vida
              </p>
            </div>

            {/* Right: mockup of generator */}
            <div className="lg:col-span-5">
              <ProMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Lo que incluye — features grid */}
      <section className="px-6 py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#1E3A8A]">Lo que recibes</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Todo lo necesario, en un solo pago</h2>
            <p className="mt-3 text-slate-600 text-base sm:text-lg leading-relaxed">
              No necesitas plantillas sueltas ni cotizaciones a abogados. El Módulo Pro cubre los puntos que la APDP fiscaliza en una PYME.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TooltipProvider delayDuration={150}>
              {INCLUYE.map((it, i) => {
                const Icon = it.icon;
                return (
                  <div
                    key={i}
                    data-testid={`mp-feature-${i}`}
                    className="group relative bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-md hover:-translate-y-0.5 hover:border-[#1E3A8A]/30 transition-all"
                  >
                    {/* Badge opcional (esquina sup. derecha) */}
                    {it.badge && (
                      <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-[10px] font-bold uppercase tracking-wide">
                        <Sparkles className="w-3 h-3" />
                        {it.badge}
                      </span>
                    )}

                    {/* Icono coloreado por feature */}
                    <div className={`w-12 h-12 rounded-xl ${it.iconBg} grid place-items-center mb-4`}>
                      <Icon className={`w-6 h-6 ${it.iconColor}`} strokeWidth={1.8} />
                    </div>

                    {/* Título + tooltip */}
                    <div className="flex items-start gap-2">
                      <h3 className="font-display text-lg font-semibold text-slate-900 flex-1">
                        {it.title}
                      </h3>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Más información sobre ${it.title}`}
                            data-testid={`mp-feature-info-${i}`}
                            className="shrink-0 -mt-0.5 w-6 h-6 rounded-full grid place-items-center text-slate-400 hover:text-[#1E3A8A] hover:bg-[#1E3A8A]/5 transition-colors"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          align="end"
                          className="max-w-xs bg-slate-900 text-white p-3 text-xs leading-relaxed shadow-xl border-0"
                        >
                          {it.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    </div>

                    <p className="mt-2 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
                      {it.desc}
                    </p>

                    {/* Línea de check incluida */}
                    <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-1.5 text-xs text-emerald-700 font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Incluido en el Módulo Pro
                    </div>
                  </div>
                );
              })}
            </TooltipProvider>
          </div>
        </div>
      </section>

      {/* Comparativa */}
      <section className="px-6 py-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-3">¿Por qué el Módulo Pro?</h2>
          <p className="text-slate-600 mb-10 max-w-2xl">Comparado con las alternativas típicas que considera una PYME.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Compare title="Abogado tradicional" tone="muted" items={[
              ["Precio", "$300.000 — $1.000.000+"],
              ["Tiempo", "Días o semanas"],
              ["Adaptado a tu negocio", "Sí"],
              ["Conforme Ley 21.719", "Sí"],
              ["Guías técnicas", "No incluidas"],
              ["Alertas si cambia la ley", "No"],
            ]} />
            <Compare title="Ley de Datos Chile" tone="highlight" items={[
              ["Precio", `$${precio.toLocaleString("es-CL")} · pago único`],
              ["Tiempo", "Minutos"],
              ["Adaptado a tu negocio", "Sí, según tus respuestas"],
              ["Conforme Ley 21.719", "Sí"],
              ["Guías técnicas", "Incluidas (WordPress, Shopify)"],
              ["Alertas si cambia la ley", "Sí, por email"],
            ]} />
            <Compare title="Plantilla de internet" tone="muted" items={[
              ["Precio", "Gratis"],
              ["Tiempo", "Inmediato"],
              ["Adaptado a tu negocio", "No"],
              ["Conforme Ley 21.719", "Casi nunca"],
              ["Guías técnicas", "No"],
              ["Alertas si cambia la ley", "No"],
            ]} />
          </div>
        </div>
      </section>

      {/* Prueba social */}
      <TestimonialsSection variant="light" />

      {/* FAQ corta */}
      <section className="px-6 py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 mb-8">Preguntas sobre el pago</h2>
          <div className="space-y-4">
            <Faq q="¿Es una suscripción o hay cobros mensuales?" a="No. Es un pago único de $14.990 CLP con Webpay que te da acceso permanente. Sin renovaciones, sin cobros futuros, sin letra chica." />
            <Faq q="¿Qué recibo al pagar?" a="Acceso permanente al generador de los 7 documentos legales (Política de Privacidad, Cláusula de Consentimiento, DPA, RAT, Cookies, Protocolo de Brechas, CCM), escaneos ilimitados, guías por plataforma (WordPress, Shopify, Wix, Jumpseller, Squarespace) y alertas de la ley cada vez que cambia — gratis de por vida." />
            <Faq q="¿Qué pasa con mis documentos en el tiempo?" a="Los documentos son tuyos para siempre. Puedes descargarlos en PDF, Markdown o TXT cuando quieras. Cuando la ley cambie, te avisamos y regeneras la versión actualizada sin costo adicional." />
            <Faq q="¿Cuál es la diferencia con contratar un abogado?" a={`Un abogado especializado en protección de datos cobra entre $1.5M y $3M CLP y tarda 2-3 semanas. Nuestro Módulo Pro cuesta $${precio.toLocaleString("es-CL")} CLP una sola vez y te entrega los documentos en menos de 30 minutos.`} />
            <Faq q="¿Cómo pago?" a="Con Webpay Plus (tarjetas de crédito o débito chilenas). El pago es seguro, procesado por Transbank y el acceso se activa automáticamente al confirmarse el pago." />
            <Faq q="¿Reemplaza a un abogado?" a="Para una PYME con operación típica (ecommerce, servicios, retail) sí cubre las obligaciones formales que pide la Ley 21.719. Para casos complejos (datos sensibles a gran escala, transferencias internacionales masivas, datos de salud) recomendamos revisión legal." />
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="px-6 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Tu PYME merece estar en regla
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-xl mx-auto">
            La Ley 21.719 entra en vigencia el 1 de diciembre de 2026. Empezar ahora cuesta una décima parte de lo que cuesta corregirlo después.
          </p>
          <button
            data-testid="mp-cta-bottom"
            onClick={ctaAction}
            className="mt-8 h-14 px-8 rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF] active:scale-[0.98] text-white font-semibold text-lg inline-flex items-center gap-2 transition-all"
          >
            {ctaText}
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="mt-5 text-xs text-slate-500">
            Si no quedas conforme con la vista previa de tu documento, no pagas. Y si la ley cambia el día después, te avisamos.
          </p>
        </div>
      </section>
    </>
  );
}

function Compare({ title, items, tone }) {
  const highlight = tone === "highlight";
  return (
    <div className={`rounded-2xl p-6 ${highlight ? "bg-[#1E3A8A] text-white border-2 border-[#1E3A8A] shadow-xl scale-[1.02]" : "bg-white border border-slate-200"}`}>
      {highlight && (
        <div className="inline-block px-2.5 py-0.5 rounded-full bg-white text-[#1E3A8A] text-[10px] font-bold uppercase tracking-wider mb-3">
          Recomendado
        </div>
      )}
      <h4 className={`font-display text-lg font-semibold ${highlight ? "text-white" : "text-slate-900"}`}>{title}</h4>
      <dl className="mt-4 space-y-3">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt className={`text-[11px] uppercase tracking-wider font-semibold ${highlight ? "text-white/60" : "text-slate-500"}`}>{label}</dt>
            <dd className={`text-sm mt-0.5 ${highlight ? "text-white" : "text-slate-700"}`}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full text-left p-5 flex items-center justify-between gap-4 hover:bg-slate-50/60">
        <h3 className="font-display font-semibold text-slate-900">{q}</h3>
        <span className={`text-slate-400 transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && <p className="px-5 pb-5 text-slate-600 leading-relaxed text-sm">{a}</p>}
    </div>
  );
}


function PriceAnchor({ label, amount, tone, hint }) {
  const toneMap = {
    muted: "bg-white/[0.03] border-white/10 text-slate-400",
    highlight: "bg-blue-500/15 border-blue-400/40 text-white ring-1 ring-blue-400/30",
    warn: "bg-rose-500/10 border-rose-500/30 text-rose-200",
  };
  return (
    <div className={`p-4 rounded-xl border ${toneMap[tone]} relative`}>
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-70 leading-tight">{label}</p>
      <p className={`mt-1.5 font-display ${tone === "highlight" ? "text-3xl text-white" : "text-2xl"} leading-tight`}>{amount}</p>
      {hint && <p className="font-mono text-[10px] mt-0.5 text-slate-400">{hint}</p>}
    </div>
  );
}

function ProMockup() {
  return (
    <div className="bg-ink-900 border border-white/10 rounded-2xl shadow-2xl shadow-blue-900/30 overflow-hidden">
      <div className="px-5 py-3 border-b border-white/5 flex items-center gap-3">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
        </div>
        <p className="font-mono text-[11px] text-slate-500">
          generador<span className="text-slate-700"> › </span>
          <span className="text-slate-300">politica-privacidad</span>
        </p>
        <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] text-emerald-400 uppercase tracking-wider">
          <span className="live-dot" /> En curso
        </span>
      </div>

      <div className="p-6">
        <p className="font-mono text-[10px] text-blue-300 uppercase tracking-widest mb-3">
          Onboarding · paso 4 / 7
        </p>
        <div className="space-y-3">
          {[
            { ok: true, label: "Razón social y RUT" },
            { ok: true, label: "Datos recolectados" },
            { ok: true, label: "Finalidades y bases legales" },
            { ok: "active", label: "Plazos de conservación" },
            { ok: false, label: "Transferencias internacionales" },
            { ok: false, label: "Decisiones automatizadas" },
            { ok: false, label: "Confirmación" },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              {s.ok === true ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              ) : s.ok === "active" ? (
                <span className="w-4 h-4 rounded-full border-2 border-blue-400 shrink-0 animate-pulse" />
              ) : (
                <span className="w-4 h-4 rounded-full border border-white/10 shrink-0" />
              )}
              <span className={`text-sm ${s.ok === true ? "text-slate-400 line-through" : s.ok === "active" ? "text-white font-medium" : "text-slate-500"}`}>
                {s.label}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
          <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">
            Estimación: 3 min restantes
          </p>
          <FileText className="w-4 h-4 text-blue-300" />
        </div>
      </div>

      <div className="border-t border-white/5 px-5 py-3 bg-ink-950/60">
        <p className="font-mono text-[9px] text-slate-600 uppercase tracking-[0.2em] mb-2">
          Documentos en cola
        </p>
        <ul className="space-y-1 font-mono text-[10px]">
          <li className="flex gap-2 text-slate-500">
            <span className="text-emerald-400">✓</span>
            <span className="text-slate-300">Política de Privacidad — listo</span>
          </li>
          <li className="flex gap-2 text-slate-500">
            <span className="text-emerald-400">✓</span>
            <span className="text-slate-300">Cláusula consentimiento — listo</span>
          </li>
          <li className="flex gap-2 text-slate-500">
            <span className="text-blue-300">⟳</span>
            <span className="text-slate-300">Catálogo de Datos (RAT) — generando…</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
